import { ReactNode, useState } from 'react';
import { Box } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { SectionCard, AnalyticsColors } from './analyticsUi';
import InfoTooltip from 'components/v2/common/infoTooltip';

interface CollapsibleSectionProps {
    title: string;
    tooltip?: string;
    defaultOpen?: boolean;
    id?: string;
    children: ReactNode;
}

const CollapsibleSection = ({
    title,
    tooltip,
    defaultOpen = true,
    id,
    children,
}: CollapsibleSectionProps) => {
    const [open, setOpen] = useState(defaultOpen);
    const contentId = id ? `${id}-content` : undefined;

    return (
        <SectionCard>
            <Box
                component="button"
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                aria-expanded={open}
                aria-controls={contentId}
                sx={{
                    background: 'transparent',
                    border: 'none',
                    p: 0,
                    m: '0 0 1.5rem 0',
                    width: '100%',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    textAlign: 'left',
                    color: AnalyticsColors.textPrimary,
                    '&::before': {
                        content: '""',
                        width: 3,
                        height: 20,
                        background: AnalyticsColors.primary,
                        borderRadius: '2px',
                        flexShrink: 0,
                    },
                    '&:focus-visible': {
                        outline: `2px solid ${AnalyticsColors.primary}`,
                        outlineOffset: '2px',
                        borderRadius: '4px',
                    },
                }}
            >
                <Box
                    component="span"
                    sx={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' }}
                >
                    {title}
                </Box>
                {tooltip && (
                    <span onClick={(e) => e.stopPropagation()}>
                        <InfoTooltip title={tooltip} />
                    </span>
                )}
                <Box
                    component="span"
                    aria-hidden="true"
                    sx={{
                        ml: 'auto',
                        display: 'inline-flex',
                        color: AnalyticsColors.textTertiary,
                        transition: 'transform 0.2s ease',
                        transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                    }}
                >
                    <ExpandMore sx={{ fontSize: 18 }} />
                </Box>
            </Box>
            {open && <div id={contentId}>{children}</div>}
        </SectionCard>
    );
};

export default CollapsibleSection;
